import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CommissionPromoBand } from "./CommissionPromoBand";
import {
  FeaturedPicksSection,
  StudioServicesSection,
} from "./FeaturedPicksSection";
import { BrandLogo } from "./BrandLogo";

const LandingPage = () => {
  const { t } = useTranslation();

  const stats = [
    { value: "80+", label: t("home.stats.paintings") },
    { value: t("home.stats.studioValue"), label: t("home.stats.studio") },
    { value: t("home.stats.originalsValue"), label: t("home.stats.originals") },
    { value: t("home.stats.spacesValue"), label: t("home.stats.spaces") },
  ];

  return (
    <div>
      <section className="relative flex min-h-[85vh] items-end overflow-hidden sm:min-h-[90vh]">
        <picture>
          <source srcSet="/homepage.webp" type="image/webp" />
          <img
            src="/homepage.jpg"
            alt={t("home.hero.heroAlt")}
            className="absolute inset-0 h-full w-full object-cover object-[center_44%]"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-t from-canvas-950/85 via-canvas-950/35 to-canvas-950/15" />

        <div className="section-container relative z-10 pb-16 pt-32 sm:pb-24">
          <div className="max-w-2xl animate-slide-up">
            <p className="mb-4 text-sm font-medium uppercase tracking-[0.2em] text-canvas-200">
              {t("home.hero.eyebrow")}
            </p>
            <div className="mb-6">
              <BrandLogo inverted />
            </div>
            <h1 className="sr-only">{t("home.hero.title")}</h1>
            <p className="mt-4 max-w-xl text-base font-medium leading-relaxed text-canvas-100/95 sm:text-lg">
              {t("home.hero.headline")}
            </p>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-canvas-300 sm:text-base">
              {t("home.hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
              <Link to="/contact#commission" className="btn-accent text-center">
                {t("home.hero.ctaCommission")}
              </Link>
              <Link
                to="/gallery?tab=canvas"
                className="btn-secondary !border-white/30 !text-white hover:!bg-white/10 text-center"
              >
                {t("home.hero.ctaCanvas")}
              </Link>
            </div>
            <p className="mt-6 max-w-md text-xs leading-relaxed text-canvas-400 sm:text-sm">
              {t("home.hero.luxuryNote")}
            </p>
          </div>
        </div>
      </section>

      <CommissionPromoBand canvasHomepage />

      <section className="border-y border-canvas-200/60 bg-white">
        <div className="section-container grid grid-cols-2 gap-6 py-10 sm:grid-cols-4 sm:py-12">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="font-display text-3xl font-semibold text-canvas-950 sm:text-4xl">
                {stat.value}
              </p>
              <p className="mt-1 text-sm text-canvas-600">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      <FeaturedPicksSection canvasHomepage />

      <StudioServicesSection canvasHomepage />

      <section className="section-container py-16 sm:py-24">
        <div className="relative overflow-hidden rounded-3xl bg-canvas-900 px-8 py-16 text-center sm:px-16 sm:py-20">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-accent/20 via-transparent to-transparent rtl:bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))]" />
          <div className="relative">
            <h2 className="font-display text-3xl font-semibold text-white sm:text-4xl">
              {t("home.cta.title")}
            </h2>
            <p className="mx-auto mt-4 max-w-lg text-canvas-300">
              {t("home.cta.subtitle")}
            </p>
            <div className="mt-8 flex flex-col flex-wrap justify-center gap-3 sm:flex-row sm:gap-4">
              <Link to="/contact#commission" className="btn-accent">
                {t("home.cta.contact")}
              </Link>
              <Link
                to="/gallery?tab=canvas"
                className="btn-secondary !border-white/20 !text-white hover:!bg-white/10"
              >
                {t("home.cta.canvas")}
              </Link>
              <Link
                to="/gallery"
                className="border-0 bg-transparent px-4 py-2 text-sm font-medium text-canvas-400 underline-offset-4 transition hover:text-white hover:underline"
              >
                {t("home.cta.fullGallery")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
